import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types/user'
import { authApi } from '@/api'

export const useAuthStore = defineStore('auth', () => {
  // 状态
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('token'))
  const isAuthenticated = computed(() => !!token.value)

  // 从localStorage加载用户信息
  const storedUser = localStorage.getItem('user')
  if (storedUser) {
    try {
      user.value = JSON.parse(storedUser)
    } catch (error) {
      console.error('Failed to parse stored user:', error)
      localStorage.removeItem('user')
    }
  }

  // 登录
  const login = async (phone: string, password: string) => {
    try {
      // 调用真实API进行登录
      const response = await authApi.login({ phone, password })
      
      if (response.code === 200 && response.data) {
        const { user: userData, token: userToken } = response.data
        
        // 更新状态
        user.value = userData
        token.value = userToken
        
        // 保存到localStorage
        localStorage.setItem('token', userToken)
        localStorage.setItem('user', JSON.stringify(userData))
        
        return { success: true, user: userData }
      } else {
        return { success: false, error: response.message || '登录失败' }
      }
    } catch (error: any) {
      console.error('Login failed:', error)
      return { 
        success: false, 
        error: error.message || '登录失败' 
      }
    }
  }

  // 注册
  const register = async (phone: string, password: string, code: string) => {
    try {
      // 调用真实API进行注册
      const response = await authApi.register({ phone, password, code })
      
      if (response.code === 200 && response.data) {
        const { user: userData, token: userToken } = response.data
        
        // 更新状态
        user.value = userData
        token.value = userToken
        
        // 保存到localStorage
        localStorage.setItem('token', userToken)
        localStorage.setItem('user', JSON.stringify(userData))
        
        return { success: true, user: userData }
      } else {
        return { success: false, error: response.message || '注册失败' }
      }
    } catch (error: any) {
      console.error('Register failed:', error)
      return { 
        success: false, 
        error: error.message || '注册失败' 
      }
    }
  }

  // 登出
  const logout = () => {
    user.value = null
    token.value = null
    
    // 清除localStorage
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    
    // 重定向到登录页
    window.location.href = '/login'
  }

  // 更新用户信息
  const updateUser = (updatedUser: Partial<User>) => {
    if (user.value) {
      user.value = { ...user.value, ...updatedUser }
      localStorage.setItem('user', JSON.stringify(user.value))
    }
  }

  const updateBalance = (balance: number) => {
    updateUser({ balance })
  }

  // 发送验证码
  const sendVerificationCode = async (phone: string) => {
    try {
      // 调用真实API发送验证码
      const response = await authApi.sendCode(phone)
      
      if (response.code === 200) {
        return { success: true }
      } else {
        return { success: false, error: response.message || '发送验证码失败' }
      }
    } catch (error: any) {
      console.error('Send verification code failed:', error)
      return { 
        success: false, 
        error: error.message || '发送验证码失败' 
      }
    }
  }

  // 刷新用户信息
  const refreshUser = async () => {
    if (!token.value) return
    
    try {
      // 调用真实API获取最新用户信息
      const response = await authApi.getProfile()
      
      if (response.code === 200 && response.data) {
        user.value = response.data.user
        localStorage.setItem('user', JSON.stringify(user.value))
      }
    } catch (error: any) {
      console.error('Refresh user failed:', error)
      // 如果token过期，自动登出
      if (error.message?.includes('401') || error.message?.includes('未提供认证令牌')) {
        logout()
      }
    }
  }

  return {
    // 状态
    user,
    token,
    isAuthenticated,
    
    // 计算属性
    userInfo: computed(() => user.value),
    
    // 方法
    login,
    register,
    logout,
    updateUser,
    updateBalance,
    sendVerificationCode,
    refreshUser
  }
})
